import React, { Fragment, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { oneOf } from 'prop-types';
import useStyles from 'hooks/useStyles';

import CourseContext from 'routes/course/components/CourseContext';

import { UnfoldLessIcon, UnfoldMoreIcon } from 'components/Icon';

import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { COURSE_TIMESTAMP_CLICK } from 'constants/gtmEvent';
import OPEN_MODE from './constants';

import useChapterOpenEventTrack from './useChapterOpenEventTrack';
import useSelectedChapter from './useSelectedChapter';

import Chapter from './components/Chapter';

import styles from './ChapterList.scss';

const INIT_MODE = OPEN_MODE.DROP_DOWN;

function ChapterList({ openMode = INIT_MODE }) {
  useStyles(styles);

  const {
    lessonEventTrackData,
    playerRef,
    currentLesson,
    toggleCurrentLessonChapterList,
    onClickCurrentLessonChapter,
    setCurrentChapterName,
  } = useContext(CourseContext);

  const dispatch = useDispatch();
  const { t } = useTranslation(['course']);

  const {
    isChapterListOpen: isOpen,
    chapters,
    timestamps,
    order: lessonOrder,
    id: lessonId,
    videoTimestamps,
  } = currentLesson;

  const commonLessonGtmData = useChapterOpenEventTrack({
    isOpen,
    lessonEventTrackData,
  });
  const { checkSelectedChapter, selectedIndex } = useSelectedChapter({
    playerRef,
    timestamps,
  });

  const isDropDown = openMode === INIT_MODE;

  const hideChapterList = chapters.length === 0 || (isOpen && !isDropDown);
  const hideToggler = isOpen && !isDropDown;

  const handleChapterClick = ({ name, index }) => {
    const timestampTime = timestamps[index];
    onClickCurrentLessonChapter(timestampTime);
    dispatch(
      sendGTMEvent({
        eventName: COURSE_TIMESTAMP_CLICK,
        data: {
          ...commonLessonGtmData,
          timestampName: name,
          itemIndex: index,
          timestampTime,
        },
      }),
    );
  };

  const togglerChildren = isOpen ? (
    <Fragment>
      {t('videoplayer.timestamp.close')}
      <UnfoldLessIcon height={16} width={16} />
    </Fragment>
  ) : (
    <Fragment>
      {t('videoplayer.timestamp.open')}
      <UnfoldMoreIcon height={16} width={16} />
    </Fragment>
  );

  useEffect(() => {
    if (setCurrentChapterName) {
      setCurrentChapterName(videoTimestamps[selectedIndex]?.description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoTimestamps, selectedIndex]);

  if (hideChapterList) {
    return null;
  }
  return (
    <Fragment>
      {!hideToggler && (
        <span
          className={styles.toggler}
          onClick={toggleCurrentLessonChapterList}
        >
          {togglerChildren}
        </span>
      )}
      {isOpen && (
        <ul>
          {chapters.map((chapter, index) => {
            const { formattedTimestamp, description } = chapter;

            return (
              <Chapter
                isSelected={checkSelectedChapter(index)}
                isDropDown={isDropDown}
                onClick={() => handleChapterClick({ name: description, index })}
                timestamp={formattedTimestamp}
                numbered={`${lessonOrder}-${index + 1}`}
                description={description}
                key={`${lessonId}_${formattedTimestamp}`}
              />
            );
          })}
        </ul>
      )}
    </Fragment>
  );
}

ChapterList.propTypes = {
  openMode: oneOf(Object.values(OPEN_MODE)).isRequired,
};

export default ChapterList;
